import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { BugReportDialog } from './BugReportDialog';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showBugReport: boolean;
}

/**
 * Error Boundary Component
 * Catches React component errors and provides a fallback UI
 * with access to bug reporting functionality
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showBugReport: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Log error details for debugging
    try {
      const errorDetails = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };
      console.error('Error details:', errorDetails);
    } catch (logError) {
      console.error('Failed to log error details:', logError);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showBugReport: false,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome}
          showBugReport={this.state.showBugReport}
          onShowBugReport={() => this.setState({ showBugReport: true })}
          onCloseBugReport={() => this.setState({ showBugReport: false })}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
  onReload: () => void;
  onGoHome: () => void;
  showBugReport: boolean;
  onShowBugReport: () => void;
  onCloseBugReport: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  onReset,
  onReload,
  onGoHome,
  showBugReport,
  onShowBugReport,
  onCloseBugReport,
}) => {
  const { translate } = useTranslation();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background p-4 overflow-auto">
      <div className="max-w-2xl w-full space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle>
            {translate('errorBoundary.title', 'Something went wrong')}
          </AlertTitle>
          <AlertDescription className="mt-2">
            {translate(
              'errorBoundary.description',
              'The application encountered an unexpected error. You can report this issue using the button below.'
            )}
          </AlertDescription>
        </Alert>

        {error && (
          <div className="bg-muted p-4 rounded-lg border">
            <h3 className="font-semibold mb-2 text-sm">
              {translate('errorBoundary.errorDetails', 'Error Details')}
            </h3>
            <pre className="text-xs overflow-auto max-h-48 font-mono">
              {error.toString()}
              {errorInfo?.componentStack && (
                <>
                  {'\n\n'}
                  {translate(
                    'errorBoundary.componentStack',
                    'Component Stack:'
                  )}
                  {'\n'}
                  {errorInfo.componentStack}
                </>
              )}
            </pre>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button onClick={onShowBugReport} variant="default">
            {translate('errorBoundary.reportBug', 'Report Bug')}
          </Button>
          <Button onClick={onReset} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            {translate('errorBoundary.tryAgain', 'Try Again')}
          </Button>
          <Button onClick={onReload} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            {translate('errorBoundary.reload', 'Reload Page')}
          </Button>
          <Button onClick={onGoHome} variant="outline">
            <Home className="h-4 w-4 mr-2" />
            {translate('errorBoundary.goHome', 'Go to Dashboard')}
          </Button>
        </div>

        {/* Bug Report Dialog - Always accessible even during crashes */}
        <BugReportDialog open={showBugReport} onClose={onCloseBugReport} />
      </div>
    </div>
  );
};
